import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { MessageSquare, BookOpen, Bookmark, GraduationCap, ShieldCheck, ArrowRight, Users, Sparkles } from "lucide-react";
import { OWNERSHIP_STATEMENT, COPYRIGHT_LINE, SUPPORT_DISCLAIMER } from "@/lib/legal";

export default function Landing() {
  const nav = useNavigate();

  const stages = [
    { n: "01", t: "Attract" },
    { n: "02", t: "Recruit" },
    { n: "03", t: "Onboard" },
    { n: "04", t: "Develop" },
    { n: "05", t: "Perform" },
    { n: "06", t: "Engage" },
    { n: "07", t: "Retain" },
    { n: "08", t: "Offboard" },
    { n: "09", t: "Intelligence" },
  ];

  return (
    <div className="min-h-screen bg-[#F5F9FE] relative overflow-hidden">
      <header className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Logo size={48} />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-[#0A2A5E] text-xl tracking-tight">LUMIS HR</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#C7761A] font-semibold">Empowering people · Simplifying HR</div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="nav-login-btn" variant="ghost" onClick={() => nav("/login")} className="text-[#0A2A5E]">Sign in</Button>
          <Button data-testid="nav-cta-btn" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white" onClick={() => nav("/register")}>
            Get Started <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </div>
      </header>

      <section className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 lg:pt-16 pb-24">
        <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FDECD0] border border-[#F5A623] mb-6">
              <ShieldCheck className="w-3.5 h-3.5 text-[#7C3F00]" />
              <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#7C3F00]">The LUMIS AI–HR Ecosystem™</span>
            </div>
            <h1 className="font-display text-[2rem] leading-[1.15] sm:text-[2.375rem] lg:text-[3.125rem] font-bold tracking-tight text-[#0A2A5E] lg:leading-[1.1] text-balance">
              HR guidance,<br/>
              <span className="italic font-serif text-[#F5A623]">with citations when available.</span>
            </h1>
            <p data-testid="positioning-statement" className="mt-5 text-lg text-slate-600 leading-relaxed max-w-2xl">Practical HR tools, workplace guidance and responsible AI readiness for managers and employees.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="hero-primary-cta" size="lg" className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white h-12 px-6" onClick={() => nav("/register")}>
                Create an Account <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button data-testid="hero-secondary-cta" size="lg" variant="outline" className="border-[#F5A623] text-[#7C3F00] hover:bg-[#FDECD0] h-12 px-6" onClick={() => nav("/login")}>
                Sign in
              </Button>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#F5A623]"/> Federal-policy grounded</div>
              <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[#F5A623]"/> Manager & Employee roles</div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="relative rounded-2xl bg-white border border-slate-200 shadow-2xl p-6 hover-lift">
              <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                <div className="w-8 h-8 rounded-full bg-[#0A2A5E] flex items-center justify-center">
                  <Sparkles className="w-4 h-4 text-[#F5A623]"/>
                </div>
                <div>
                  <div className="text-xs font-bold text-[#0A2A5E]">LUMIS HR Assistant</div>
                  <div data-testid="assistant-label" className="text-[10px] uppercase tracking-widest text-[#C7761A]">Source-cited · Human review and verification required</div>
                </div>
              </div>
              <div className="space-y-4 text-sm">
                <div data-testid="demo-example-label" className="text-[10px] uppercase tracking-widest text-slate-500">Source-cited example · not a determination for any employee</div>
                <div className="p-3 rounded-xl bg-slate-100 text-slate-800" data-testid="demo-question">
                  What information should an HR manager review when an employee requests intermittent FMLA leave?
                </div>
                <div className="p-3 rounded-xl bg-[#FFF7E8] border border-[#FBD38D] text-slate-800 leading-relaxed" data-testid="demo-answer">
                  Under the Department of Labor's FMLA regulations, intermittent leave is <span className="highlight-mark">FMLA leave taken in separate blocks of time due to a single qualifying reason</span>, and for a serious health condition <span className="highlight-mark">there must be a medical need for leave</span> that is best accommodated through an intermittent or reduced schedule <span className="whitespace-nowrap"><a href="https://www.ecfr.gov/current/title-29/subtitle-B/chapter-V/subchapter-C/part-825/subpart-B/section-825.202" target="_blank" rel="noopener noreferrer" data-testid="demo-citation-link" className="citation-badge underline decoration-dotted">29 CFR § 825.202</a>.</span> Review the stated reason and, where required, the health-care provider certification against your organization's leave policy. Coverage and this employee's eligibility have not been determined; this example does not approve leave or replace organizational policy or qualified HR review.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="relative z-10 bg-[#0A2A5E] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div data-testid="framework-label" className="text-xs font-semibold uppercase tracking-[0.3em] text-[#F5A623] mb-3">The 9-Stage LUMIS AI Across Employee Lifecycle Model™</div>
          <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl">
            LUMIS AI Across Employee Lifecycle Model™ — <span className="italic font-serif text-[#F5A623]">people first, always.</span>
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-9 gap-3 mt-12">
            {stages.map((s) => (
              <div key={s.n} className="p-4 rounded-xl bg-[#0F3F8A]/50 border border-[#1E5AB8] hover:border-[#F5A623] transition">
                <div className="font-mono text-xs text-[#F5A623] mb-1">{s.n}</div>
                <div className="font-display font-bold text-white">{s.t}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="human-judgment-statement" className="relative z-10 bg-[#0A2A5E] border-t border-[#1E5AB8] py-5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-display text-lg sm:text-xl font-bold text-white text-balance">LUMIS HR is designed to <span className="italic font-serif font-normal text-[#F5A623] whitespace-nowrap">support—not replace</span>—human judgment.</p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: MessageSquare, t: "Ask anything HR", d: "Federal policies, forms and SOPs—with guidance linked to relevant sources." },
            { icon: BookOpen, t: "Federal Library", d: "Pre-seeded federal PDFs from DOL, EEOC, OSHA, OPM, USCIS + your uploads." },
            { icon: Bookmark, t: "Highlight & Save", d: "Select any passage, save it with its source, revisit whenever." },
            { icon: GraduationCap, t: "Responsible AI Readiness", d: "Practical workplace training on AI policies, confidential information, human review, bias, inaccurate outputs, shadow AI and verification." },
          ].map((f) => (
            <div key={f.t} className="p-6 rounded-2xl bg-white border border-slate-200 hover-lift">
              <div className="w-10 h-10 rounded-lg bg-[#FDECD0] flex items-center justify-center mb-4">
                <f.icon className="w-5 h-5 text-[#7C3F00]"/>
              </div>
              <h3 className="font-display font-bold text-lg text-[#0A2A5E]">{f.t}</h3>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
        <p data-testid="trust-smallprint" className="mt-6 text-[13px] sm:text-sm text-slate-500 leading-relaxed">Verified sources when available · Private employee preparation remains separate from information intentionally shared · Human review required</p>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="rounded-3xl bg-gradient-to-br from-[#0A2A5E] via-[#0F3F8A] to-[#1E5AB8] p-10 sm:p-16 text-white relative overflow-hidden">
          <div className="relative z-10 grid lg:grid-cols-[1fr_auto] gap-10 items-center">
          <div className="max-w-2xl">
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">
              Compliance excellence. <span className="text-[#F5A623]">Workforce readiness.</span>
            </h2>
            <p className="mt-4 text-lg text-blue-50/90">
              Join HR teams using LUMIS HR to answer, cite, and lead with confidence.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button data-testid="cta-signup-btn" size="lg" className="bg-[#F5A623] text-[#0A2A5E] hover:bg-[#FBD38D] h-12 px-6 font-bold" onClick={() => nav("/register")}>
                Create account
              </Button>
              <Button data-testid="cta-signin-btn" size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 h-12 px-6 bg-transparent" onClick={() => nav("/login")}>
                Sign in
              </Button>
            </div>
          </div>
          <div data-testid="cta-logo-container" className="flex items-center justify-center p-6 sm:p-8 lg:p-10 opacity-20 select-none" aria-hidden="true">
            <img src="/lhs-mark.png" alt="" draggable={false} data-testid="cta-logo-mark" className="block w-40 sm:w-52 lg:w-64 h-auto max-w-full object-contain" />
          </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div data-testid="footer-copyright" className="max-w-xl">{COPYRIGHT_LINE}</div>
          <div className="flex gap-4">
            <Link to="/terms" data-testid="footer-terms" className="hover:text-[#0A2A5E]">Terms</Link>
            <Link to="/privacy" data-testid="footer-privacy" className="hover:text-[#0A2A5E]">Privacy</Link>
            <Link to="/login" className="hover:text-[#0A2A5E]">Sign in</Link>
            <Link to="/register" className="hover:text-[#0A2A5E]">Get started</Link>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-3 space-y-1 text-xs text-slate-400 leading-relaxed">
          <div data-testid="founder-line">Developed by Abadaya Lashley, MPA, founder of LUMIS HR Solutions and author of <em>Smarter HR</em> and <em>AI Literacy Is Not Enough</em>.</div>
          <div data-testid="footer-disclaimer">{SUPPORT_DISCLAIMER}</div>
          <div data-testid="footer-ownership">{OWNERSHIP_STATEMENT}</div>
        </div>
      </footer>
    </div>
  );
}
